<template>
    <div class="mentor-filter__wrap">
        <template v-if="errorMessage">
            {{ errorMessage }}
        </template>
        <ul class="find-mentor-hero__filter-list grid list-unstyled cols-4@lg cols-3@md">
            <!-- Name -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.name') }}
                </div>
                <input
                    type="text"
                    class="form-field"
                    :placeholder="$t('mentor_filter.placeholders.name')"
                    @input="debounceFunction('textFiltersChanged', 's', $event)"
                    v-model="filterValues['s']"
                >
            </li>
            <!-- Type -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.venture_type') }}
                </div>
                <!--Type taxonomy isn't available at the moment-->
                <vc4a-select
                    :placeholder="$t('mentor_filter.labels.venture_type')"
                    :options="mentor_type"
                    :selected="filterValues.mentor_type"
                    @onInput="termsFiltersChanged('mentor_type', $event)"
                    :summarised="true"
                    track-by="item_id"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Venture Stage -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.stage') }}
                </div>
                <vc4a-select
                    :placeholder="$t('venture_stage')"
                    :options="venture_stage"
                    :selected="filterValues.venture_stage"
                    @onInput="termsFiltersChanged('venture_stage', $event)"
                    :summarised="true"
                    track-by="item_id"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Language -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.language') }}
                </div>
                <vc4a-select
                    :placeholder="$t('mentor_filter.labels.language')"
                    :options="spoken_languages"
                    :selected="filterValues.spoken_languages"
                    @onInput="termsFiltersChanged('spoken_languages', $event)"
                    :summarised="true"
                    track-by="item_id"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Sector -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.sector') }}
                </div>
                <vc4a-select
                    :placeholder="$t('sector')"
                    :options="sectors"
                    :selected="filterValues.sectors"
                    @onInput="termsFiltersChanged('sectors', $event)"
                    group-values="childItems"
                    group-label="group"
                    :group-select="true"
                    :summarised="true"
                    track-by="slug"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Based in -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.country') }}
                </div>
                <vc4a-select
                    :placeholder="$t('mentor_filter.labels.country')"
                    :options="base_location"
                    :selected="filterValues.base_location"
                    group-label="group"
                    group-values="childItems"
                    :group-select="true"
                    @onInput="termsFiltersChanged('base_location', $event)"
                    :summarised="true"
                    track-by="slug"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Based in -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.focus_locations') }}
                </div>
                <vc4a-select
                    :placeholder="$t('mentor_filter.placeholders.focus_locations')"
                    :options="focus_locations"
                    :selected="filterValues.focus_locations"
                    group-label="group"
                    group-values="childItems"
                    :group-select="true"
                    @onInput="termsFiltersChanged('focus_locations', $event)"
                    :summarised="true"
                    track-by="slug"
                    :label="getTermLabel()"
                />
            </li>
            <!-- Topics -->
            <li>
                <div class="label">
                    {{ $t('mentor_filter.labels.topics') }}
                </div>
                <vc4a-select
                    :placeholder="$t('mentor_filter.labels.topics')"
                    :options="topic"
                    :selected="filterValues.topic"
                    @onInput="termsFiltersChanged('topic', $event)"
                    :summarised="true"
                    track-by="item_id"
                    :label="getTermLabel()"
                />
            </li>
        </ul>
        <Flash v-if="flash.message" :type="flash.type ? flash.type : 'info'" :message="flash.message"/>
    </div>
    
    <!-- Mentor cards at initial page load are from php and is only replaced with Vue after filtering or sorting is done -->
    <template v-if="!isInitialLoad">
        
        <!-- Loading state -->
        <template v-if="queryInProgress">
            <teleport to="#mentors-index-wrapper">
                <div v-for="item in 6"
                    class="mentor-card mentor-card--loading" aria-hidden="true"
                >
                    <div class="loader-item" style="height:12.5rem;"></div>
                    <div class="mentor-card__body">
                        <div class="loader-item" style="width:60%; height:24px; margin-bottom:.5rem;"></div>
                        <div class="loader-item-wrap" style="margin-bottom:.5rem;">
                            <div class="loader-item" style="width:10%;height:20px;"></div>
                            <div class="loader-item" style="width:70%;height:20px;"></div>
                        </div>
                        <div class="loader-item" style="height: 60px;"></div>
                    </div>
                </div>
            </teleport>
        </template>
        <template v-else>
            <teleport to="#mentors-index-wrapper">
                
                <!--  Mentor cards -->
                <template v-if="mentors.length">
                    <mentor-card
                        v-for="(mentor, index) in mentors"
                        :mentor="mentor"
                        :key="'mentor-card-' + index"
                    />
                </template>
                
                <!-- Empty state (after filtering) -->
                <template v-else>
                    <Alert
                        type="warning"
                        message="No mentors found"
                    />
                </template>
            </teleport>
            
            <!-- Pagination -->
            <teleport to="#mentors-index-pagination">
                <Pagination
                    v-if="paginationPropsObj && mentors.length"
                    :current-page="paginationPropsObj.currentPage"
                    :found-posts="paginationPropsObj.foundPosts"
                    :max-num-pages="paginationPropsObj.maxNumPages"
                />
            </teleport>
        </template>
    </template>
</template>

<script>
import Vc4aSelect from "src/views/utility/vc4a-select";
import { GET_MENTORS } from "src/graphql/mentor";
import { getTermLabel } from "src/shared/terms";
import Flash from "src/views/common/flash";
import i18n from 'src/i18n';
import MentorCard from 'src/views/mentor/card';
import Pagination from 'src/views/layout/pagination';
import Alert from 'src/views/common/alert';

import _ from "lodash";

export default {
    name: "MentorFilter",
    components: {
        Pagination,
        Alert,
        MentorCard,
        Vc4aSelect,
        Flash
    },
    props: {
        postsPerPage: {
            type: [Number, String],
            default: 18
        },
        currentPage: {
            type: [Number, String],
            default: 1
        },
        partnerId: {
            type: [Number, String],
            default: 0,
            required: false,
        },
        termsJson: {
            type: String,
            default: ''
        },
        filterPrefill: {
            type: String,
            default: ''
        },
    },
    data(){
        return {
            filterPanelVisible: false,
            terms: JSON.parse(this.termsJson) || {},
            prefill: JSON.parse(this.filterPrefill) || {},
            queryInProgress: false,
            errorMessage: '',
            toggleIsActive: false,
            mentor_type: [],
            sectors: [],
            base_location: [],
            focus_locations: [],
            topic: [],
            venture_stage: [],
            spoken_languages: [],
            // live field values
            filterValues: {
                mentor_type: [],
                sectors: [],
                topic: [],
                venture_stage: [],
                spoken_languages: [],
                base_location: [],
                focus_locations: [],
                s: '', // name-search
            },
            // list of filters (by slug) that will only use strings, never arrays
            textBasedFilterList: [
                's', // name-search
            ],
            hierarchical: [
                'focus_locations'
            ],
            where: {
                'sectors': {
                    'parentId': 0
                }
            },
            sortByTermId: [
                'mentor_support',
                'venture_stage',
                'mentor_type',
            ],
            flash: {
                message: '',
                type: ''
            },
            loadingTerms: 0,
            url: {},
            urlParams: {},
            localCurrentPage: 1, // Local mutable version of currentPage
            sortBy: {
                label: 'Recommended',
                value: 'score_desc',
            },
            mentors: [],
            isInitialLoad: true, // On initial load, Vue component is not active, Content is rendered via php
            paginationPropsObj: {}
        }
    },
    async beforeMount() {
        this.loadingTerms++;
        await this.retrieveTerms();
        this.loadingTerms--;
    },
    mounted() {
        if( this.currentPage > 1 ) {
            this.localCurrentPage = parseInt( this.currentPage );
        }
        this.url = new URL(window.location);
        this.urlParams = new URLSearchParams(this.url.search);
        let search = window.location.search;
        if( search.replace( '?', '' ) !== '' ) {
            this.filterPanelVisible = true;
        }

        this.sortBy = this.sortOptions[0];
        let _vm = this;
        window.addEventListener("mentor-filter-sort", function(event) {
            _vm.sortByChanged(event.detail);
        }, false);
    },
    methods: {
        setupFilters(taxonomy) {
            let terms = this.getActiveFilterForTaxonomy( taxonomy );
            if( ( this.prefill.tax || '' ) === taxonomy ) {
                terms = [this.prefill.term];
            }

            if (this.textBasedFilterList.includes(taxonomy)) { // if one of the filters that returns/accepts a string
                // set the filter/field value from the url param
                this.filterValues[taxonomy] = this.urlParams.get(taxonomy);
                if( ( this.prefill.tax || '' ) === taxonomy ) {
                    this.filterValues[taxonomy] = terms;
                }
            } else if(!this.textBasedFilterList.includes(taxonomy)) {

                // if one of the filters that returns/accepts an array
                if( terms ) {

                    // Preselect filter terms from URL
                    // Checks (first element) if options are grouped/nested
                    let isGrouped = Object.keys(this[taxonomy][0]).includes('childItems');
                    terms.forEach((term) => {
                        if (isGrouped) {
                            this[taxonomy].forEach((groupedItem) => {
                                let itemFound = groupedItem.childItems.find(obj => obj.slug === term);
                                (itemFound) ? this.filterValues[taxonomy].push(itemFound) : false;
                            })
                        } else {
                            let itemFound = this[taxonomy].find(obj => obj.slug === term);
                            (itemFound) ? this.filterValues[taxonomy].push(itemFound) : false;
                        }
                    });
                }
            }

        },
        resetPagination() {
            // Reset pagination.
            this.urlParams.delete('paged');
            this.localCurrentPage = 1;
            this.url.pathname = this.url.pathname.replace(/\/page\/\d+/, '');

            window.history.replaceState({}, '', `${this.url.pathname}?${this.urlParams}`);

            this.filterMentors();
        },
        // Feed me a function and I'll debounce it
        debounceFunction: _.debounce(
            function (functionSlug, filterSlug, event) {
                switch (functionSlug) {
                    case 'textFiltersChanged':
                        this.textFiltersChanged(filterSlug, event)
                    // other functions can be added as new cases
                }
            },
        300),
        textFiltersChanged(filterSlug, event) {
            if(!event.target) {
                return;
            }

            const textFilterStr = event.target.value;

            if( textFilterStr.length >=3 ) {
                this.urlParams.set(filterSlug, textFilterStr);
            } else if( textFilterStr.length < 1 ) {
                this.urlParams.delete(filterSlug);
            }

            this.resetPagination();
        },
        termsFiltersChanged(taxonomy, event) {
            if( this.loadingTerms > 0) {
                return;
            }

            let terms = this.getActiveFilterForTaxonomy(taxonomy)
            terms = [...event.map(e => e.slug)]
            terms = terms.filter( term => term != null );

            // Compare terms from event and terms stored in our variable.
            // If length differs, we need to fetch from GraphQL.
            // This is to prevent unnecessary request on page load.
            let filterTerms = this.filterValues[taxonomy].map(e => e.slug);
            if( filterTerms.length > 0 && terms.length > 0 && filterTerms.length === terms.length ) {
                let triggerFilter = false;
                event.filter(e => {
                    if (filterTerms.indexOf(e.slug) === -1) {
                        triggerFilter = true;
                    }
                });

                if( ! triggerFilter ) {
                    return;
                }
            }

            if( terms.length ) {
                this.urlParams.set(taxonomy, terms)
            } else {
                this.urlParams.delete(taxonomy)
            }

            this.resetPagination();
        },
        getActiveFilterForTaxonomy(taxonomy) {
            this.url = new URL(window.location);
            this.urlParams = new URLSearchParams(this.url.search);
            let terms = this.urlParams.get(taxonomy);

            return terms ? terms.split(',') : null;
        },
        retrieveTerms() {
            Object.keys(this.filterValues).forEach( taxonomy => {
                try {
                    this[taxonomy] = this.terms[taxonomy];
                } catch(e) {
                    this[taxonomy] = [];
                }
                this.setupFilters(taxonomy);
            } );
        },
        displayFlash( flash ) {
            this.flash = flash;
            setTimeout(() => this.flash = { message: '', type: 'info' }, 3000 );
        },
        filterMentors() {
            let mentorsWrapper = document.getElementById('mentors-index-wrapper');
            let mentorsPagination = document.getElementById('mentors-index-pagination');
            
            // Clear php content and replace with Vue content
            if (this.isInitialLoad) {
                mentorsWrapper.innerHTML = ''
                mentorsPagination.innerHTML = ''
                this.isInitialLoad = false; // Indicates original php content on page load has been cleared and content visible is vue
            }
            
            this.queryInProgress = true;
            
            let where = {
                url: window.location.href,
                paged: this.urlParams.get('paged') ? parseInt( this.urlParams.get('paged') ) : parseInt( this.localCurrentPage ),
                orderby: this.sortBy.orderby,
                order: this.sortBy.order,
            }

            Object.keys(this.filterValues).forEach( taxonomy => {
                where[taxonomy] = this.urlParams.get(taxonomy);
            } );

            if( parseInt( this.partnerId ) > 0 ) {
                where.partner_id = parseInt( this.partnerId );
            }

            this.$apollo.query( {
                query: GET_MENTORS,
                variables: {
                    first: parseInt( this.postsPerPage ),
                    where: where,
                },
            } ).then( ( { data, loading } ) => {
                if( ! loading ) {
                    this.mentors = [];
                    if (data.mentors.edges.length) {
                        data.mentors.edges.map(mentor => {
                            this.mentors.push(mentor.node);
                        });
                        this.paginationPropsObj = {
                            foundPosts: data.mentors.pageInfo.resultTotal,
                            maxNumPages: data.mentors.pageInfo.resultMaxPage,
                            currentPage: this.localCurrentPage,
                        }
                    }
                }
            } ).catch( ( error ) => {
                // Error.
                console.error(error);
                this.displayFlash({
                    message: error.message.replace('GraphQL error:', ''),
                    type: 'warning'
                });
            }).finally(() => {
                this.queryInProgress = false;
            });
        },
        getTermLabel() {
            return getTermLabel();
        },
        sortByChanged(event) {
            if( event === undefined ) {
                return;
            }
            this.sortBy = {...event};
            this.filterMentors();
        }
    },
    computed: {
        sortOptions() {
            return [
                {
                    label: i18n.global.t( 'sort.score_desc' ),
                    value: 'score_desc',
                    orderby: 'menu_order',
                    order: 'DESC',
                },
                {
                    label: i18n.global.t( 'sort.date_desc' ),
                    value: 'date_desc',
                    orderby: 'date',
                    order: 'DESC',
                },
                {
                    label: i18n.global.t( 'sort.date_asc' ),
                    value: 'date_asc',
                    orderby: 'date',
                    order: 'ASC',
                },
                {
                    label: i18n.global.t( 'sort.name_asc' ),
                    value: 'name_asc',
                    orderby: 'title',
                    order: 'ASC',
                },
                {
                    label: i18n.global.t( 'sort.name_desc' ),
                    value: 'name_desc',
                    orderby: 'title',
                    order: 'DESC',
                },
            ];
        },
    }
}
</script>
